import { useMemo, type ReactElement } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@baseline/web/supabase";
import DOMPurify from "dompurify";
import { FiArrowLeft } from "react-icons/fi";
import { formatDate } from "../../lib/format";

export const Route = createFileRoute("/emails/$emailId")({
	component: EmailDetail,
});

function EmailDetail(): ReactElement {
	const { emailId } = Route.useParams();

	const { data: email, isLoading } = useQuery({
		queryKey: ["ingest-email", emailId],
		queryFn: async () => {
			const { data, error } = await supabase
				.from("ingest_emails")
				.select("*")
				.eq("id", emailId)
				.single();
			if (error) throw error;
			return data;
		},
	});

	const sanitizedHtml = useMemo(() => {
		if (!email?.body_html) return null;
		return DOMPurify.sanitize(email.body_html);
	}, [email?.body_html]);

	if (isLoading) {
		return (
			<div className="mx-auto max-w-[960px] px-6 py-8">
				<div className="text-fg-muted text-sm">Loading...</div>
			</div>
		);
	}

	if (!email) {
		return (
			<div className="mx-auto max-w-[960px] px-6 py-8">
				<div className="text-fg-muted text-sm">Email not found.</div>
			</div>
		);
	}

	return (
		<div className="mx-auto max-w-[960px] px-6 py-8">
			<Link
				to="/"
				search={{ tab: "emails" }}
				data-testid="back-to-inbox"
				className="text-fg-muted hover:text-accent mb-6 inline-flex items-center gap-1 text-sm transition-colors"
			>
				<FiArrowLeft />
				Back to inbox
			</Link>

			<div className="border-line mb-6 rounded-lg border p-6">
				<h1
					className="text-fg text-xl font-medium"
					data-testid="email-subject"
				>
					{email.subject || "(no subject)"}
				</h1>
				<div className="text-fg-muted mt-2 flex items-center gap-3 text-sm">
					<span>
						From:{" "}
						<strong>{email.from_name || email.from_address}</strong>
						{email.from_name && (
							<span className="text-fg-faint ml-1">
								{"<"}
								{email.from_address}
								{">"}
							</span>
						)}
					</span>
					<span className="text-fg-faint">
						{formatDate(email.received_at)}
					</span>
				</div>
			</div>

			<div className="border-line rounded-lg border p-6">
				{sanitizedHtml ? (
					<div
						className="prose prose-sm max-w-none"
						dangerouslySetInnerHTML={{ __html: sanitizedHtml }}
					/>
				) : email.body_text ? (
					<pre className="text-fg font-sans text-sm whitespace-pre-wrap">
						{email.body_text}
					</pre>
				) : (
					<p className="text-fg-faint text-sm italic">No content</p>
				)}
			</div>
		</div>
	);
}
